/**
 * Authored set pieces — issue #1 phase 5.
 *
 * THE PROBLEM THIS EXISTS FOR. We are 100% procedural, and that is why nothing
 * in the world is memorable. Noita is not: it lays hand-authored *pixel scenes*
 * over its Wang tiles, and every player of it can describe the same handful of
 * rooms to you. A generator tuned as carefully as ours produces terrain that is
 * always plausible and never *specific* — you remember that a band was vertical
 * and green, not that you fought something on a fallen arcade over a flooded
 * cistern. Six stamps buy the second kind of memory, and they cost the
 * generator nothing at runtime because they are data.
 *
 * WHAT A SET PIECE IS NOT. It is not a room archetype (`rooms.ts`) — those are
 * parameterised shapes the herringbone tiler instantiates, and their whole
 * virtue is that they vary. A set piece is the same every time you find it. It
 * is also not a sanctum or a rest node: those are *services*, guaranteed and
 * navigable; this is scenery with a fight in it, and the player is not told
 * where it is.
 *
 * THE CONNECTIVITY CONTRACT, which is the load-bearing part. A stamp overwrites
 * whatever the noise and the corridor router left inside its rect, so an
 * unconstrained stamp is a way to seal a band. Every piece therefore declares a
 * `through` band — a full-width open corridor at a stated row, at least the
 * player's 22-cell body tall — which is carved LAST, after every other op, and
 * which `worldgen.ts` extends into approach corridors on both sides and marks
 * as `Struct.CRITICAL_PATH`. A piece that walls off its own corridor is a
 * compile-time-shaped bug, and `tests/setpiece.test.ts` asserts the property on
 * the generated cells rather than trusting the declaration.
 *
 * DETERMINISM. `setPieceAt` is pure in `(seed, band)`, exactly like `sanctumAt`:
 * no generation order, no clock, no neighbour queries. Which of the six lands
 * in a band is a hash, so a seed's world is fixed, and a chunk that is evicted
 * and regenerated comes back byte-identical.
 */

import { hash2 } from '../core/rng';

/** What a carve op writes into the kind grid. */
export type PieceKind = 'solid' | 'open' | 'shell' | 'platform';

/**
 * A material role, resolved against the biome's palette at dressing time.
 *
 * Roles rather than materials, because a set piece has to be *the same room* in
 * twelve biomes and the room is the shape, not the substance. A flooded cistern
 * in the Sunken Vault is water and oil; the same stamp in the Cinder March is
 * lava and water, and it is a different problem for the same reason the biome
 * is a different place.
 *
 * The split below is not cosmetic. `liquid`/`liquid2`/`fill`/`hazard` are
 * poured into open cells; `glow`/`accent`/`shell` are seams written into cells
 * that are already solid. Pouring a rigid glow material into open space would
 * put an obstacle in a corridor, and painting a liquid onto a wall does
 * nothing at all.
 */
export type PieceRole = 'liquid' | 'liquid2' | 'fill' | 'hazard' | 'glow' | 'accent' | 'shell';

export function rolePoursIntoAir(role: PieceRole): boolean {
  return role === 'liquid' || role === 'liquid2' || role === 'fill' || role === 'hazard';
}

export type PieceOp =
  | { op: 'rect'; x: number; y: number; w: number; h: number; k: PieceKind }
  | { op: 'ellipse'; x: number; y: number; rx: number; ry: number; k: PieceKind }
  | { op: 'dress'; x: number; y: number; w: number; h: number; role: PieceRole };

export interface SetPiece {
  id: string;
  label: string;
  /** Stamp extent in cells. 64 or 128 on each axis — see the file header. */
  w: number;
  h: number;
  /**
   * Top row of the guaranteed through-corridor, stamp-local.
   *
   * Its *floor* is the row below the corridor, so `throughY + throughH` must be
   * inside the stamp: a corridor whose floor is outside the piece is a corridor
   * whose floor is whatever the noise left, which is the failure this whole
   * contract exists to prevent.
   */
  throughY: number;
  /** Corridor height. Must clear the 22-cell body of docs/02 §3.2. */
  throughH: number;
  ops: PieceOp[];
}

/**
 * Minimum corridor height.
 *
 * 26, not 23. The body is 22, but `deposits` is deliberately not gated on the
 * protection flag (`worldgen.ts` explains why: gating it put a step in the
 * floor every twenty cells), so a drift of up to three cells of the biome's own
 * fill settles on the corridor's floor slab. A 23-cell corridor with three
 * cells of coal dust in it is a 20-cell corridor, and the player does not fit.
 */
export const THROUGH_MIN_H = 26;

/**
 * How far a set piece may sit off the spine.
 *
 * Small on purpose, and the opposite of a rest node's 420. A rest node is a
 * reward for leaving the descent; a set piece is the descent being worth
 * walking down, so the player must run into it without looking for it. ±150
 * plus a 128-wide stamp keeps every piece inside one screen of the axis.
 */
const PIECE_DRIFT = 150;

/**
 * Clearance kept from both band lines.
 *
 * A sanctum's full extent (chamber, shell and both approach shafts) reaches
 * about 200 cells above its line and 180 below it. 300 is comfortably clear of
 * that in both directions, so a set piece can never be stamped through the one
 * structure in the game that is not allowed to have a hole in it.
 */
const BAND_MARGIN = 300;

// ============================================================== the six pieces
//
// Read each as: fill the stamp solid, carve the room, add the features, and let
// `worldgen.ts` cut the through-corridor last. Coordinates are stamp-local with
// the origin at the top-left.

const COLLAPSED_GALLERY: SetPiece = {
  id: 'collapsed_gallery',
  label: 'The Fallen Arcade',
  w: 128,
  h: 128,
  throughY: 96,
  throughH: 26,
  ops: [
    { op: 'rect', x: 0, y: 0, w: 128, h: 128, k: 'solid' },
    { op: 'rect', x: 6, y: 10, w: 116, h: 108, k: 'open' },
    // Three columns that held, and the floor they used to hold up — one-way, so
    // the upper level is a place you drop from and can climb back to.
    { op: 'rect', x: 24, y: 10, w: 7, h: 56, k: 'shell' },
    { op: 'rect', x: 60, y: 10, w: 7, h: 56, k: 'shell' },
    { op: 'rect', x: 96, y: 10, w: 7, h: 56, k: 'shell' },
    { op: 'rect', x: 6, y: 64, w: 52, h: 4, k: 'platform' },
    { op: 'rect', x: 76, y: 64, w: 46, h: 4, k: 'platform' },
    // ...and the part that did not hold.
    { op: 'ellipse', x: 34, y: 104, rx: 26, ry: 13, k: 'solid' },
    { op: 'ellipse', x: 94, y: 106, rx: 21, ry: 11, k: 'solid' },
    { op: 'dress', x: 20, y: 56, w: 88, h: 8, role: 'fill' },
    { op: 'dress', x: 24, y: 12, w: 7, h: 52, role: 'glow' },
    { op: 'dress', x: 96, y: 12, w: 7, h: 52, role: 'glow' },
  ],
};

const DROWNED_CISTERN: SetPiece = {
  id: 'drowned_cistern',
  label: 'The Drowned Cistern',
  w: 128,
  h: 128,
  // The route is over the water, not through it: a corridor at the bottom of a
  // tank is a corridor the biome's liquid closes on tick one.
  throughY: 30,
  throughH: 26,
  ops: [
    { op: 'rect', x: 0, y: 0, w: 128, h: 128, k: 'solid' },
    { op: 'rect', x: 8, y: 8, w: 112, h: 116, k: 'shell' },
    { op: 'rect', x: 12, y: 12, w: 104, h: 108, k: 'open' },
    { op: 'rect', x: 12, y: 62, w: 104, h: 4, k: 'platform' },
    // Both liquids, offset rather than overlapping, for the reason biomes.ts
    // note (3) gives: overlapping means the player arrives to the aftermath.
    { op: 'dress', x: 14, y: 74, w: 58, h: 44, role: 'liquid' },
    { op: 'dress', x: 80, y: 92, w: 34, h: 26, role: 'liquid2' },
    { op: 'dress', x: 8, y: 8, w: 112, h: 4, role: 'shell' },
  ],
};

const LAMP_YARD: SetPiece = {
  id: 'lamp_yard',
  label: 'The Lamp Yard',
  w: 128,
  h: 128,
  throughY: 94,
  throughH: 26,
  ops: [
    { op: 'rect', x: 0, y: 0, w: 128, h: 128, k: 'solid' },
    { op: 'rect', x: 6, y: 8, w: 116, h: 112, k: 'open' },
    { op: 'rect', x: 18, y: 8, w: 10, h: 84, k: 'shell' },
    { op: 'rect', x: 46, y: 8, w: 10, h: 84, k: 'shell' },
    { op: 'rect', x: 74, y: 8, w: 10, h: 84, k: 'shell' },
    { op: 'rect', x: 102, y: 8, w: 10, h: 84, k: 'shell' },
    // The one piece that is *about* the light (#31): four pillars veined top to
    // bottom in the biome's own emitter, so a band's identity arrives as a
    // colour before any of its materials are on screen.
    { op: 'dress', x: 18, y: 12, w: 10, h: 76, role: 'glow' },
    { op: 'dress', x: 46, y: 12, w: 10, h: 76, role: 'glow' },
    { op: 'dress', x: 74, y: 12, w: 10, h: 76, role: 'glow' },
    { op: 'dress', x: 102, y: 12, w: 10, h: 76, role: 'glow' },
    { op: 'dress', x: 6, y: 8, w: 116, h: 5, role: 'hazard' },
  ],
};

const OSSUARY_NICHES: SetPiece = {
  id: 'ossuary_niches',
  label: 'The Niches',
  w: 64,
  h: 64,
  throughY: 36,
  throughH: 26,
  ops: [
    { op: 'rect', x: 0, y: 0, w: 64, h: 64, k: 'solid' },
    { op: 'rect', x: 2, y: 2, w: 60, h: 60, k: 'shell' },
    // Two rows of alcoves cut into the back wall, each holding one of the
    // biome's accents. Small enough that you have to walk up to read them.
    { op: 'rect', x: 6, y: 6, w: 10, h: 10, k: 'open' },
    { op: 'rect', x: 20, y: 6, w: 10, h: 10, k: 'open' },
    { op: 'rect', x: 34, y: 6, w: 10, h: 10, k: 'open' },
    { op: 'rect', x: 48, y: 6, w: 10, h: 10, k: 'open' },
    { op: 'rect', x: 6, y: 20, w: 10, h: 10, k: 'open' },
    { op: 'rect', x: 20, y: 20, w: 10, h: 10, k: 'open' },
    { op: 'rect', x: 34, y: 20, w: 10, h: 10, k: 'open' },
    { op: 'rect', x: 48, y: 20, w: 10, h: 10, k: 'open' },
    { op: 'dress', x: 6, y: 6, w: 52, h: 10, role: 'accent' },
    { op: 'dress', x: 6, y: 20, w: 52, h: 10, role: 'glow' },
  ],
};

const BROKEN_FORGE: SetPiece = {
  id: 'broken_forge',
  label: 'The Broken Engine',
  w: 64,
  h: 64,
  throughY: 34,
  throughH: 26,
  ops: [
    { op: 'rect', x: 0, y: 0, w: 64, h: 64, k: 'solid' },
    { op: 'rect', x: 4, y: 4, w: 56, h: 56, k: 'open' },
    { op: 'rect', x: 20, y: 8, w: 24, h: 22, k: 'shell' },
    // The flue. Open to the top edge of the stamp, so the piece reads as
    // something that vented somewhere rather than as a sealed box.
    { op: 'rect', x: 28, y: 0, w: 9, h: 10, k: 'open' },
    { op: 'dress', x: 22, y: 26, w: 20, h: 4, role: 'glow' },
    { op: 'dress', x: 6, y: 56, w: 22, h: 4, role: 'liquid' },
    { op: 'dress', x: 40, y: 56, w: 18, h: 4, role: 'fill' },
  ],
};

const ASH_FLUE: SetPiece = {
  id: 'ash_flue',
  label: 'The Flue',
  w: 64,
  h: 64,
  throughY: 32,
  throughH: 26,
  ops: [
    { op: 'rect', x: 0, y: 0, w: 64, h: 64, k: 'solid' },
    { op: 'rect', x: 6, y: 6, w: 52, h: 54, k: 'open' },
    // A shaft open at both edges, rungs every 12 cells — half the 26-cell
    // sanctum pitch, because this one is climbed under pressure.
    { op: 'rect', x: 26, y: 0, w: 13, h: 64, k: 'open' },
    { op: 'rect', x: 26, y: 8, w: 13, h: 2, k: 'platform' },
    { op: 'rect', x: 26, y: 20, w: 13, h: 2, k: 'platform' },
    { op: 'rect', x: 26, y: 56, w: 13, h: 2, k: 'platform' },
    { op: 'dress', x: 8, y: 6, w: 48, h: 5, role: 'hazard' },
    { op: 'dress', x: 8, y: 56, w: 48, h: 4, role: 'fill' },
  ],
};

/**
 * The roster, in no significant order — which one a band gets is a hash, so
 * array order carries no meaning and reordering this list must not reshuffle a
 * seed's world.
 */
export const SET_PIECES: readonly SetPiece[] = [
  COLLAPSED_GALLERY,
  DROWNED_CISTERN,
  LAMP_YARD,
  OSSUARY_NICHES,
  BROKEN_FORGE,
  ASH_FLUE,
];

export interface PlacedSetPiece {
  band: number;
  piece: SetPiece;
  /** Stamp origin, top-left, in world cells. */
  x: number;
  y: number;
  /** World y of the through-corridor's top row and of the floor under it. */
  throughTop: number;
  throughFloor: number;
}

/**
 * The set piece in a band. Pure in `(seed, band)`.
 *
 * One per band, which is exactly the acceptance criterion — "≥ 1 set piece per
 * band" — and deliberately not more. Six authored rooms spread over eight bands
 * already repeat; scattering three per band would turn the thing that is
 * supposed to be a landmark into wallpaper, which is the failure mode of every
 * procedural game that adds handmade content by the yard.
 */
export function setPieceAt(seed: number, band: number, bandHeight: number): PlacedSetPiece {
  const h = hash2(band, 0x5e7, seed ^ 0x5e7717);
  const piece = SET_PIECES[h % SET_PIECES.length];

  const x = ((h >>> 7) % (PIECE_DRIFT * 2 + 1)) - PIECE_DRIFT - (piece.w >> 1);

  const lo = band * bandHeight + BAND_MARGIN;
  const span = bandHeight - BAND_MARGIN * 2 - piece.h;
  const y = lo + ((h >>> 17) % (span > 1 ? span : 1));

  return {
    band,
    piece,
    x,
    y,
    throughTop: y + piece.throughY,
    throughFloor: y + piece.throughY + piece.throughH,
  };
}

/** Vertical extent of a placement, as `[top, bottom]` world y. */
export function setPieceSpanY(p: PlacedSetPiece): [number, number] {
  return [p.y, p.y + p.piece.h - 1];
}

/** Every set piece whose stamp intersects `[y0, y1]`. */
export function setPiecesOverlapping(
  seed: number,
  bands: number,
  bandHeight: number,
  y0: number,
  y1: number,
): PlacedSetPiece[] {
  const out: PlacedSetPiece[] = [];
  let lo = Math.floor(y0 / bandHeight) - 1;
  let hi = Math.floor(y1 / bandHeight) + 1;
  if (lo < 0) lo = 0;
  if (hi > bands - 1) hi = bands - 1;
  for (let b = lo; b <= hi; b++) {
    const p = setPieceAt(seed, b, bandHeight);
    const [top, bottom] = setPieceSpanY(p);
    if (bottom >= y0 && top <= y1) out.push(p);
  }
  return out;
}

/** All of them, top to bottom. */
export function allSetPieces(seed: number, bands: number, bandHeight: number): PlacedSetPiece[] {
  const out: PlacedSetPiece[] = [];
  for (let b = 0; b < bands; b++) out.push(setPieceAt(seed, b, bandHeight));
  return out;
}
